import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { 
  Calendar,
  ArrowRight,
  BookOpen,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Translated Article Card component
const TranslatedArticleCard = ({ article, language, translateContent, formatDate, readMore }) => {
  const [translatedTitle, setTranslatedTitle] = useState(article.title);
  const [translatedContent, setTranslatedContent] = useState(article.content);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    const translateArticle = async () => {
      if (language === 'ro') {
        setTranslatedTitle(article.title);
        setTranslatedContent(article.content);
        return;
      }

      setIsTranslating(true);
      try {
        const [title, content] = await Promise.all([
          translateContent(article.title, 'article_title', `${article.id}_title`),
          translateContent(article.content, 'article_content', `${article.id}_content`)
        ]);
        setTranslatedTitle(title);
        setTranslatedContent(content);
      } catch (error) {
        console.error('Translation error:', error);
      } finally {
        setIsTranslating(false);
      }
    };

    translateArticle();
  }, [article, language, translateContent]);

  const getExcerpt = (content, maxLength = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };

  return (
    <Link 
      to={`/jurnal/${article.id}`}
      className="group bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
    >
      {/* Image */}
      {article.image_url ? (
        <div className="aspect-[4/3] overflow-hidden">
          <img 
            src={article.image_url} 
            alt={translatedTitle}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      ) : (
        <div className="aspect-[4/3] bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
          <BookOpen className="w-12 h-12 text-amber-300" />
        </div>
      )}
      
      {/* Content */}
      <div className="p-5">
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
          <Calendar className="w-3 h-3" />
          {formatDate(article.created_at)}
        </div>
        
        {isTranslating ? (
          <div className="flex items-center gap-2 mb-3">
            <Loader2 className="w-4 h-4 animate-spin text-sky-500" />
            <span className="text-slate-400 text-sm">...</span>
          </div>
        ) : (
          <>
            <h3 className="font-bold text-slate-900 mb-2 group-hover:text-sky-600 transition-colors line-clamp-2">
              {translatedTitle}
            </h3>
            
            <p className="text-slate-600 text-sm line-clamp-2 mb-3">
              {getExcerpt(translatedContent)}
            </p>
          </>
        )}
        
        <span className="inline-flex items-center gap-1 text-sky-600 font-medium text-sm">
          {readMore}
          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
};

const JournalPreviewSection = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language, translateContent } = useLanguage();

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/articles?limit=3`);
        setArticles(response.data || []);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const formatDate = (dateStr) => {
    const locale = language === 'ro' ? 'ro-RO' : language === 'de' ? 'de-DE' : 'en-US';
    return new Date(dateStr).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Translations for all 3 languages
  const translations = {
    ro: {
      sectionTitle: 'Din Jurnalul Meu',
      sectionSubtitle: 'Experiențe și gânduri din călătoria mea',
      viewAll: 'Vezi Toate Articolele',
      readMore: 'Citește'
    },
    en: {
      sectionTitle: 'From My Journal',
      sectionSubtitle: 'Experiences and thoughts from my journey',
      viewAll: 'View All Articles',
      readMore: 'Read'
    },
    de: {
      sectionTitle: 'Aus Meinem Tagebuch',
      sectionSubtitle: 'Erfahrungen und Gedanken aus meiner Reise',
      viewAll: 'Alle Artikel Ansehen',
      readMore: 'Lesen'
    }
  };

  const t = translations[language] || translations.en;

  // Don't render if no articles
  if (loading || articles.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <BookOpen className="w-4 h-4" />
            {t.sectionTitle}
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {t.sectionTitle}
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            {t.sectionSubtitle}
          </p>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {articles.map(article => (
            <TranslatedArticleCard
              key={article.id}
              article={article}
              language={language}
              translateContent={translateContent}
              formatDate={formatDate}
              readMore={t.readMore}
            />
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center">
          <Link 
            to="/jurnal"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-full font-medium transition-colors"
          >
            {t.viewAll}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default JournalPreviewSection;
